import { cacheService } from './cache.service.js'
import { apiService } from './api.service.js'
import { AUTH_CONSTANTS } from '../constants/auth.constants.js'

/**
 * Serviço de cache para saldos mensais pré-calculados pelo backend
 * Implementa padrão cache-first com TTL de 10 minutos (CacheService.DEFAULT_TTL),
 * deduplicação de requisições simultâneas via loadingPromises Map,
 * recálculo via endpoint POST /saldos-mensais/recalcular,
 * e fallback offline.
 *
 * Chave de cache: saldo_mensal_MM_AAAA (por período)
 * Eventos disparados: saldo-mensal-cache-updated
 *
 * Economia: 1 leitura Firestore (documento pré-calculado) vs N leituras (transações brutas)
 */
export class SaldoMensalCacheService {
  constructor() {
    /** @type {string} Prefixo das chaves de cache no localStorage */
    this.cacheKeyPrefix = 'saldo_mensal_'

    /** @type {string} Endpoint do backend para saldos mensais */
    this.endpoint = 'saldos-mensais'

    /**
     * Map para deduplicação de promises
     * Previne múltiplas requisições simultâneas ao mesmo recurso
     * @type {Map<string, Promise>}
     */
    this.loadingPromises = new Map()

    // Escuta evento de CRUD de transações para invalidação automática
    window.addEventListener('transacoes-cache-updated', this._handleTransacaoUpdated.bind(this))
  }

  /**
   * Obtém saldo do mês atual com estratégia cache-first
   * Usa cacheService.isValid() para verificação centralizada de TTL (10 minutos)
   * Implementa deduplicação via loadingPromises Map
   * Se offline e sem cache válido, retorna cache expirado ou null
   *
   * @returns {Promise<Object|null>} Saldo mensal do mês atual ou null se offline sem cache
   */
  async obterMesAtual() {
    const hoje = new Date()
    const mes = String(hoje.getMonth() + 1).padStart(2, '0')
    const ano = String(hoje.getFullYear())
    const cacheKey = `${this.cacheKeyPrefix}${mes}_${ano}`

    // Cache-first: verifica validade com TTL padrão de 10 minutos
    if (cacheService.isValid(cacheKey)) {
      return cacheService.get(cacheKey)
    }

    // Fallback offline: retorna cache expirado se disponível
    if (!navigator.onLine) {
      const cacheExpirado = cacheService.get(cacheKey)
      return cacheExpirado || null
    }

    // Deduplicação: reutiliza Promise em andamento para o mesmo período
    const dedupeKey = `mes_atual_${mes}_${ano}`
    if (this.loadingPromises.has(dedupeKey)) {
      return this.loadingPromises.get(dedupeKey)
    }

    // Cache miss: busca do backend
    const promise = this._loadMesAtual(mes, ano, cacheKey)
    this.loadingPromises.set(dedupeKey, promise)

    try {
      const result = await promise
      return result
    } finally {
      this.loadingPromises.delete(dedupeKey)
    }
  }

  /**
   * Obtém histórico de saldos mensais com estratégia cache-first
   * Faz GET com query param ?meses=N e cacheia cada período individualmente.
   * Usa cacheService.isValid() para verificação centralizada de TTL
   * Se offline, retorna períodos disponíveis em cache (mesmo expirados).
   *
   * @param {number} meses - Quantidade de meses para buscar (padrão 6)
   * @returns {Promise<Array>} Lista de saldos mensais ordenados por período
   */
  async obterHistorico(meses = 6) {
    // Fallback offline: retorna períodos disponíveis em cache
    if (!navigator.onLine) {
      return this._obterHistoricoDoCache(meses)
    }

    // Deduplicação: reutiliza Promise em andamento
    const dedupeKey = `historico_${meses}`
    if (this.loadingPromises.has(dedupeKey)) {
      return this.loadingPromises.get(dedupeKey)
    }

    // Verifica se todos os períodos estão em cache válido (TTL 10 min)
    const periodosEsperados = this._calcularPeriodos(meses)
    const todosEmCache = periodosEsperados.every(p => {
      const key = `${this.cacheKeyPrefix}${p.mes}_${p.ano}`
      return cacheService.isValid(key)
    })

    if (todosEmCache) {
      return periodosEsperados.map(p => {
        const key = `${this.cacheKeyPrefix}${p.mes}_${p.ano}`
        return cacheService.get(key)
      })
    }

    // Cache miss: busca do backend
    const promise = this._loadHistorico(meses)
    this.loadingPromises.set(dedupeKey, promise)

    try {
      const result = await promise
      return result
    } finally {
      this.loadingPromises.delete(dedupeKey)
    }
  }

  /**
   * Recalcula saldo mensal via endpoint POST /saldos-mensais/recalcular
   * Envia transações do cache local para o backend aplicar regras de negócio
   * (divisão compartilhada, agrupamento por categoria, patrimônio total)
   * e retorna o saldo mensal completo calculado.
   *
   * Dispara evento saldo-mensal-cache-updated após atualização do cache.
   *
   * @param {string} mes - Mês no formato 'MM' (ex: '01')
   * @param {string} ano - Ano no formato 'AAAA' (ex: '2025')
   * @returns {Promise<Object|null>} Saldo mensal recalculado ou null em caso de erro
   */
  async recalcular(mes, ano) {
    const mesStr = String(mes).padStart(2, '0')
    const anoStr = String(ano)
    const cacheKey = `${this.cacheKeyPrefix}${mesStr}_${anoStr}`

    // Obtém transações do cache local para enviar ao backend
    const transacoesCacheKey = `transacoes_${mesStr}_${anoStr}`
    const transacoesCache = cacheService.get(transacoesCacheKey) || []

    // Monta payload para o endpoint de recálculo
    const payload = {
      mes: parseInt(mesStr, 10),
      ano: parseInt(anoStr, 10),
      transacoesCache
    }

    const response = await apiService.post(`${this.endpoint}/recalcular`, payload)
    const dados = response.data?.data || response.data || response

    // Atualiza cache com resultado do recálculo
    cacheService.set(cacheKey, dados)

    // Dispara evento de atualização para componentes da UI
    window.dispatchEvent(new CustomEvent('saldo-mensal-cache-updated', {
      detail: { mes: mesStr, ano: anoStr }
    }))

    return dados
  }

  /**
   * Invalida cache de um período específico e notifica componentes
   * Remove a entrada do localStorage e dispara evento para que
   * componentes (Dashboard, Relatórios) recarreguem dados.
   *
   * @param {string} mes - Mês no formato '01' a '12' (com zero à esquerda)
   * @param {string} ano - Ano no formato 'AAAA'
   */
  invalidarPeriodo(mes, ano) {
    const cacheKey = `${this.cacheKeyPrefix}${mes}_${ano}`
    cacheService.remove(cacheKey)

    // Notifica componentes que escutam invalidação de saldo mensal
    window.dispatchEvent(new CustomEvent('saldo-mensal-cache-invalidado', {
      detail: { mes, ano }
    }))
  }

  /**
   * Invalida todo o cache de saldos mensais
   * Remove todas as entradas com prefixo 'saldo_mensal_' do localStorage.
   * Útil para logout ou troca de usuário.
   */
  invalidarTudo() {
    if (!cacheService.currentUserUid) return

    const userPrefix = `equilibra_cache_${cacheService.currentUserUid}_${this.cacheKeyPrefix}`
    Object.keys(localStorage)
      .filter(key => key.startsWith(userPrefix))
      .forEach(key => localStorage.removeItem(key))
  }

  /**
   * Força recarregamento do saldo do mês atual
   * Invalida o cache do mês corrente e busca dados atualizados do backend.
   * Útil após operações que afetam o mês atual.
   *
   * @returns {Promise<Object|null>} Saldo mensal atualizado
   */
  async recarregar() {
    const hoje = new Date()
    const mes = String(hoje.getMonth() + 1).padStart(2, '0')
    const ano = String(hoje.getFullYear())

    this.invalidarPeriodo(mes, ano)
    return this.obterMesAtual()
  }

  /**
   * Handler para evento de CRUD de transações
   * Extrai o período afetado do event.detail e invalida o cache correspondente.
   * Isso garante que após criar/editar/excluir uma transação, o resumo
   * financeiro do período seja recarregado na próxima consulta.
   *
   * @param {CustomEvent} event - Evento 'transacoes-cache-updated' com detail { mes, ano }
   * @private
   */
  _handleTransacaoUpdated(event) {
    const detail = event?.detail
    if (detail && detail.mes && detail.ano) {
      this.invalidarPeriodo(detail.mes, detail.ano)
    }
  }

  /**
   * Retorna objeto de saldo mensal com valores zerados
   * Usado como fallback quando o backend retorna array vazio
   * (mês sem cálculo) para que componentes tenham estrutura consistente.
   *
   * @returns {Object} Saldo mensal com todos os campos zerados
   * @private
   */
  _getSaldoVazio() {
    const hoje = new Date()
    return {
      mes: hoje.getMonth() + 1,
      ano: hoje.getFullYear(),
      totalReceitas: 0,
      totalDespesas: 0,
      saldoLiquido: 0,
      patrimonioTotal: 0,
      receitasPorCategoria: {},
      despesasPorCategoria: {},
      gastoCompartilhado: 0,
      gastoPessoal: 0,
      totalTransacoes: 0
    }
  }

  /**
   * Carrega saldo do mês atual do backend
   * Faz GET /api/saldos-mensais?meses=1 e cacheia o resultado.
   * Se backend retorna array vazio, retorna saldo vazio sem cachear.
   * Dispara evento saldo-mensal-cache-updated após cachear com sucesso.
   *
   * @param {string} mes - Mês no formato '01' a '12'
   * @param {string} ano - Ano no formato 'AAAA'
   * @param {string} cacheKey - Chave para armazenar no cache
   * @returns {Promise<Object>} Saldo mensal do período
   * @private
   */
  async _loadMesAtual(mes, ano, cacheKey) {
    try {
      // Busca 2 meses para garantir que o mês atual está no array,
      // mesmo quando há um documento mais recente (ex: mês futuro de dados residuais)
      const response = await apiService.get(this.endpoint, { meses: 2 })
      const dados = response.data

      // Backend retorna array de saldos
      const saldos = Array.isArray(dados) ? dados : (dados?.data || dados || [])

      // Se array vazio, retorna saldo vazio sem cachear
      if (!saldos || saldos.length === 0) {
        return this._getSaldoVazio()
      }

      // Localizar o saldo que corresponde ao mês/ano atual.
      // O backend pode retornar meses futuros como primeiro elemento (dados residuais
      // de testes, ordenados por data decrescente). Busca explicitamente o mês correto.
      const mesNum = parseInt(mes, 10)
      const anoNum = parseInt(ano, 10)
      const saldoAtual = saldos.find(s => s.mes === mesNum && s.ano === anoNum) || saldos[0]

      cacheService.set(cacheKey, saldoAtual)

      // Dispara evento de atualização para componentes da UI
      window.dispatchEvent(new CustomEvent('saldo-mensal-cache-updated', {
        detail: { mes, ano }
      }))

      return saldoAtual
    } catch (error) {
      // Em caso de erro, tenta retornar cache expirado como fallback
      const cacheExpirado = cacheService.get(cacheKey)
      if (cacheExpirado) return cacheExpirado

      return null
    }
  }

  /**
   * Carrega histórico de saldos mensais do backend
   * Faz GET /api/saldos-mensais?meses=N e cacheia cada período individualmente.
   * Dispara evento saldo-mensal-cache-updated para cada período cacheado.
   *
   * @param {number} meses - Quantidade de meses para buscar
   * @returns {Promise<Array>} Lista de saldos mensais
   * @private
   */
  async _loadHistorico(meses) {
    try {
      const response = await apiService.get(this.endpoint, { meses })
      const dados = response.data

      // Backend retorna array de saldos
      const saldos = Array.isArray(dados) ? dados : (dados?.data || dados || [])

      // Cacheia cada período individualmente
      for (const saldo of saldos) {
        if (saldo && saldo.mes && saldo.ano) {
          const mes = String(saldo.mes).padStart(2, '0')
          const ano = String(saldo.ano)
          const cacheKey = `${this.cacheKeyPrefix}${mes}_${ano}`
          cacheService.set(cacheKey, saldo)
        }
      }

      // Dispara evento de atualização após cachear histórico
      if (saldos.length > 0) {
        window.dispatchEvent(new CustomEvent('saldo-mensal-cache-updated', {
          detail: { tipo: 'historico' }
        }))
      }

      return saldos
    } catch (error) {
      // Em caso de erro, tenta retornar períodos disponíveis em cache
      return this._obterHistoricoDoCache(meses)
    }
  }

  /**
   * Obtém histórico de saldos a partir do cache local (fallback offline)
   * Percorre os períodos esperados e retorna os que estiverem em cache,
   * mesmo que expirados. Melhor mostrar dados antigos que tela vazia.
   *
   * @param {number} meses - Quantidade de meses para buscar
   * @returns {Array} Lista de saldos disponíveis em cache
   * @private
   */
  _obterHistoricoDoCache(meses) {
    const periodos = this._calcularPeriodos(meses)
    const saldos = []

    for (const periodo of periodos) {
      const cacheKey = `${this.cacheKeyPrefix}${periodo.mes}_${periodo.ano}`
      const cached = cacheService.get(cacheKey)
      if (cached) {
        saldos.push(cached)
      }
    }

    return saldos
  }

  /**
   * Calcula os períodos (mes/ano) dos últimos N meses a partir do mês atual
   * Usado para verificar cache e montar fallback offline.
   *
   * @param {number} meses - Quantidade de meses para calcular
   * @returns {Array<{mes: string, ano: string}>} Lista de períodos no formato { mes: '05', ano: '2025' }
   * @private
   */
  _calcularPeriodos(meses) {
    const periodos = []
    const hoje = new Date()

    for (let i = 0; i < meses; i++) {
      const data = new Date(hoje.getFullYear(), hoje.getMonth() - i, 1)
      periodos.push({
        mes: String(data.getMonth() + 1).padStart(2, '0'),
        ano: String(data.getFullYear())
      })
    }

    return periodos
  }
}

// Instância singleton exportada para uso em toda a aplicação
export const saldoMensalCacheService = new SaldoMensalCacheService()
